import React, { useEffect, useState } from 'react';
import { CHARACTERS, LOCATIONS } from '../logic/gameData';

export default function Story({ gameLogic, onLeaveStory }) {
  const [content, setContent] = useState(null);
  const [location, setLocation] = useState(null);

  useEffect(() => {
    const current = gameLogic.getCurrentContent();
    setContent(current.content);
    setLocation(current.location);
  }, []);

  const handleAdvance = () => {
    const result = gameLogic.advance();
    if (result.isGameOver) {
      onLeaveStory();
      return;
    }

    const current = gameLogic.getCurrentContent();
    setContent(current.content);
    setLocation(current.location);
  };

  const handleChoice = (index) => {
    const result = gameLogic.makeChoice(index);
    if (result.isGameOver) {
      onLeaveStory();
      return;
    }

    const current = gameLogic.getCurrentContent();
    setContent(current.content);
    setLocation(current.location);
  };

  if (!content) return null;

  return (
    <div className="relative h-full w-full overflow-hidden">
      {/* Background Layers */}
      <div className="absolute inset-0 z-0 pointer-events-none">
        <div className="absolute inset-0 bg-gradient-to-b from-black/30 via-transparent to-black/60 z-10" />
        <div className="absolute inset-0 bg-[radial-gradient(circle,transparent_40%,rgba(0,0,0,0.4)_100%)] z-10" />
        <img
          src={gameLogic.getSpriteUrl(LOCATIONS[location].spriteId)}
          alt={LOCATIONS[location].name}
          className="w-full h-full object-cover"
        />
      </div>

      {/* Character */}
      {content.type === 'dialog' && !content.noSpeakerSprite && (
        <div 
          className="absolute z-20 pointer-events-none transition-opacity duration-300"
          style={{
            bottom: '-40px',
            left: '50%',
            transform: 'translateX(-50%)',
            width: '208px',
            height: '312px'
          }}
        >
          <div className="absolute inset-0 bg-gradient-to-t from-black/20 to-transparent" />
          <img
            src={gameLogic.getSpriteUrl(CHARACTERS[content.speaker].spriteId, content.variation)}
            alt={CHARACTERS[content.speaker].name}
            className="w-full h-full animate-[fadeIn_0.3s_ease-in-out]"
          />
        </div>
      )}

      {/* Dialog/Choice UI */}
      <div className="absolute bottom-0 left-0 right-0 p-4 z-30">
        <div className="bg-gradient-to-br from-emerald-900/90 to-slate-900/95 p-5 rounded-xl 
          border border-emerald-400/20 shadow-[0_0_30px_rgba(0,0,0,0.5)]
          backdrop-blur-sm animate-[slideUp_0.3s_ease-out]">
          {content.type === 'dialog' && (
            <>
              <div className="text-2xl mb-3 font-['Crimson_Pro'] font-bold text-emerald-300
                [text-shadow:_0_0_10px_rgba(52,211,153,0.3)]">
                {CHARACTERS[content.speaker].name}
              </div>
              <div className="text-lg mb-4 font-['Quicksand'] text-gray-100 leading-relaxed
                [text-shadow:_0_0_20px_rgba(255,255,255,0.1)]">
                {content.text}
              </div>
              <button
                onClick={handleAdvance}
                className="text-emerald-400/70 text-sm font-['Quicksand'] hover:text-emerald-300 
                  transition-colors duration-300 animate-[pulse_2s_ease-in-out_infinite]">
                tap to continue...
              </button>
            </>
          )}

          {content.type === 'narration' && (
            <>
              <div className="text-lg italic mb-4 font-['Crimson_Pro'] text-gray-200 leading-relaxed
                [text-shadow:_0_0_20px_rgba(255,255,255,0.1)]">
                {content.text}
              </div>
              <button
                onClick={handleAdvance}
                className="text-emerald-400/70 text-sm font-['Quicksand'] hover:text-emerald-300 
                  transition-colors duration-300 animate-[pulse_2s_ease-in-out_infinite]">
                tap to continue...
              </button>
            </>
          )}

          {content.type === 'choice' && (
            <>
              <div className="text-lg mb-5 font-['Quicksand'] text-gray-200 leading-relaxed
                [text-shadow:_0_0_20px_rgba(255,255,255,0.1)]">
                {content.text}
              </div>
              <div className="space-y-3">
                {content.options.map((option, index) => (
                  <button
                    key={index}
                    onClick={() => handleChoice(index)}
                    className="w-full p-4 bg-gradient-to-r from-emerald-800/60 to-emerald-900/60 
                      hover:from-emerald-700/60 hover:to-emerald-800/60
                      rounded-lg text-left transition-all duration-300 
                      border border-emerald-400/20 hover:border-emerald-400/40
                      text-gray-100 hover:text-white text-base font-['Quicksand']
                      shadow-[0_0_20px_rgba(0,0,0,0.3)]
                      hover:shadow-[0_0_25px_rgba(0,0,0,0.4)]
                      transform hover:translate-x-1">
                    {option.text}
                  </button>
                ))}
              </div>
            </>
          )}
        </div>
      </div>

      {/* Animations */}
      <style jsx>{`
        @keyframes fadeIn {
          from { opacity: 0; }
          to { opacity: 1; }
        }
        @keyframes slideUp {
          from { transform: translateY(20px); opacity: 0; }
          to { transform: translateY(0); opacity: 1; }
        }
        @keyframes pulse {
          0%, 100% { opacity: 1; }
          50% { opacity: 0.6; }
        }
      `}</style>
    </div>
  );
}
